/**
 * Interfaces and types for FileSystem Proxy
 * Used for automatic journaling of file operations in Pyodide
 */

/**
 * Rules for determining file persistence
 */
export interface PersistenceRules {
  /** File name prefixes that should be persistent (e.g., ['cache_', 'saved_']) */
  prefixes?: string[];

  /** File extensions that should be persistent (e.g., ['.cache', '.persistent']) */
  extensions?: string[];

  /** Directory paths where files should be persistent (e.g., ['/persistent/']) */
  paths?: string[];

  /** Maximum file size to persist (bytes). Files larger than this are temporary. */
  maxSize?: number;
}

/**
 * Configuration for FileSystem Proxy
 *
 * Default behavior: "Delete by Default"
 * - Files created in Pyodide are temporary and deleted on context change
 * - Only explicitly marked files (cache_, persistent=true) are kept
 * All configuration fields are optional and only apply if explicitly set
 */
export interface FileSystemProxyConfig {
  /**
   * Default persistence for all files
   * Default: false (DELETE BY DEFAULT - files are temporary)
   * Set to true to keep all files by default
   */
  defaultPersistent?: boolean;

  /**
   * Rules for determining which files should be persistent
   * Default: undefined (NO rules - everything is temporary)
   * Programmer configures explicitly if files should persist
   * Example: { prefixes: ['cache_', 'saved_'] }
   */
  persistenceRules?: PersistenceRules;

  /**
   * Optional: Working directory path (e.g., '/home/pyodide')
   * Note: By default, ALL paths are journaled regardless of workingDir
   * This is kept for reference only, not used for filtering
   */
  workingDir?: string;

  /**
   * Optional: Paths to exclude from journaling (e.g., ['/dev', '/proc'])
   * Default: [] (empty array - journal everything)
   * Only files starting with these paths will be excluded
   */
  excludePaths?: string[];

  /**
   * Optional: Enable detailed logging of file operations to console
   * Default: false
   * Useful for debugging file journaling
   */
  enableLogging?: boolean;

  /**
   * Optional: Only journal files with these extensions (e.g., ['.csv', '.xlsx'])
   * Default: undefined (journal all file types)
   * If set, only files with matching extensions will be journaled
   */
  includeExtensions?: string[];

  /**
   * Optional: Minimum file size to journal (in bytes)
   * Default: 0 (journal all files regardless of size)
   * Files smaller than this will not be journaled
   */
  minFileSize?: number;
}

/**
 * Type of file system operation
 */
export enum FileOperationType {
  WRITE = 'write',
  OPEN = 'open',
  CLOSE = 'close',
  RENAME = 'rename',
  UNLINK = 'unlink',
  MKDIR = 'mkdir',
  RMDIR = 'rmdir'
}

/**
 * Entry for tracking file descriptor writes
 * Used to accumulate writes until file is closed
 */
export interface WriteTrackingEntry {
  /** File path */
  path: string;

  /** Total bytes written so far */
  bytesWritten: number;

  /** Timestamp when file was opened */
  openTime: number;

  /** File descriptor (if applicable) */
  fd?: number;
}

/**
 * Result of shouldJournal check
 */
export interface JournalDecision {
  /** Should this operation be journaled? */
  shouldJournal: boolean;

  /** Reason for the decision (for debugging) */
  reason?: string;
}

/**
 * Metadata extracted from file operation
 */
export interface FileOperationMetadata {
  /** Normalized file path */
  path: string;

  /** File type/extension */
  fileType: string;

  /** Size in bytes */
  size: number;

  /** Operation type */
  operation: FileOperationType;

  /** Timestamp */
  timestamp: number;
}
